import Avatar from "@/components/Avatar";
import Menu, { type MenuItem, type MenuListEntry } from "@/components/Menu";
import { MENU_PANEL_WIDTH_WIDE } from "@/components/Menu/menuLayout";
import NavLogo from "./NavLogo";
import { defaultNavLinks } from "@/layouts/AppLayout/navLinks";
import useCapabilities, {
  DOMAIN,
  PERMISSION,
  useCanViewTasksBool,
  useHasCapabilityBool,
  useHasRoles
} from "@/hooks/useCapabilities";
import { Path, useNavigate } from "@/router";
import { useLocation } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import NavItem from "../NavItem";
import { useWindowSize } from "@/hooks/useWindowSize";
import { isLargeDesktop } from "@/assets/theme/sizes";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { ROLE } from "@/providers/AuthorizationProvider/types";
import Badge from "@/components/Badge";
import Button from "@/components/Button";
import GlobalToolbarIconButton from "@/components/Button/GlobalToolbarIconButton";
import { ActionShortcutTooltip } from "@/components/KeyboardShortcutHint";
import type { EffectiveDisplayUser } from "@/hooks/useEffectiveDisplayUser";
import { useFlags } from "@/hooks/useFeatureFlags/useFlags";
import { canAccessMcpPlayground } from "@/features/mcp-playground/access";
import { canAccessAgentChatLogs } from "@/features/agent-chat-logs/access";
import { canAccessM360Configuration } from "@/pages/configuration/access";
import useOpenTaskCount from "@/hooks/useOpenTaskCount";
import useOpenTasksShortcut from "@/hooks/useOpenTasksShortcut";
import { usePrimarySection } from "@/hooks/usePrimarySection";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getInternalReturnUrl } from "@/providers/AssumeUserProvider/storage";
import { hrefWithStoredDefaultTab } from "@/lib/defaultTab";
import BackendSourceIndicator from "./BackendSourceIndicator";
import CompactPrimaryNavMenu from "./CompactPrimaryNavMenu";
import type { PrimaryNavTabItem } from "@/layouts/AppLayout/primaryNavTabItems";
import { getGoNavChordKey, primaryNavItemSlug } from "@/layouts/AppLayout/primaryNavTabItems";
import {
  COMPACT_GLOBAL_NAVBAR_AVATAR_SIZE_CLASS,
  COMPACT_GLOBAL_NAVBAR_LOGO_SIZE_CLASS,
  compactGlobalToolbarActionsGapClassName,
  compactGlobalToolbarIconClusterGapClassName
} from "./compactNavbarChrome";
import { isDesktopNavLinkActive } from "./desktopNav";
import { useDesktopNavOverflow } from "./useDesktopNavOverflow";
import GoNavSlidingGhost from "./GoNavSlidingGhost";
import classNames from "classnames";
import KeyboardShortcutsDialog from "@/components/KeyboardShortcutsDialog";
import useKeyboardShortcutsHelpShortcut from "@/hooks/useKeyboardShortcutsHelpShortcut";
import type { KeyboardShortcutEntry } from "@/lib/keyboardShortcutCatalog";
import { buildKeyboardShortcutEntries } from "@/lib/keyboardShortcutCatalog";
import M360FeedbackDialog from "./M360FeedbackDialog";

type TopActionBarProps = {
  interior?: boolean;
  user: EffectiveDisplayUser;
  logout(): void;
  actions?: React.ReactNode;
  hasSecondaryNav?: boolean;
  primaryNav?: {
    tabItems: PrimaryNavTabItem[];
    domains: DOMAIN[];
  };
  primaryNavGoNav?: {
    menuOpen: boolean;
    onMenuOpenChange: (open: boolean) => void;
    goNavActive: boolean;
  };
  keyboardShortcutEntries?: KeyboardShortcutEntry[];
};

export default function TopActionBar({
  user,
  logout,
  interior,
  actions,
  hasSecondaryNav: _hasSecondaryNav,
  primaryNav,
  primaryNavGoNav,
  keyboardShortcutEntries: keyboardShortcutEntriesProp = []
}: TopActionBarProps) {
  const { picture, name, email, department } = user;
  const flags = useFlags();
  const { assumedUser, stopAssumingUser } = useAssumeUser();
  const [keyboardShortcutsOpen, setKeyboardShortcutsOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const toggleKeyboardShortcuts = useCallback(() => {
    setKeyboardShortcutsOpen((open) => !open);
  }, []);
  const openFeedback = useCallback(() => {
    setFeedbackOpen(true);
  }, []);
  const { width } = useWindowSize();
  const isLargeScreen = isLargeDesktop(width);
  const { isCompactLayout: isMobileDisplay } = useIsCompactLayout();
  useKeyboardShortcutsHelpShortcut({
    onToggle: toggleKeyboardShortcuts,
    helpDialogOpen: keyboardShortcutsOpen,
    enabled: !isMobileDisplay
  });
  useEffect(() => {
    if (isMobileDisplay) setKeyboardShortcutsOpen(false);
  }, [isMobileDisplay]);

  const { capabilities, loaded } = useCapabilities();
  const isUniversityStaff = useHasRoles([ROLE.UNIVERSITY_STAFF]);
  const showStaffNav = loaded && !isUniversityStaff;
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const canAssumeUser = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.assumeUser });
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPrimarySection, primarySectionId } = usePrimarySection();
  const openTaskCount = useOpenTaskCount();
  const canViewTasks = useCanViewTasksBool();
  const canViewPeople = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.viewPeople });
  const canViewFacilities = useHasCapabilityBool({ domain: DOMAIN.FACILITIES, permission: PERMISSION.viewFacilities });
  const canViewMcpPlayground = canAccessMcpPlayground(canViewPeople, canViewFacilities);
  const canViewAgentChatLogs = canAccessAgentChatLogs(isGlobalAdmin);
  const canViewM360Configuration = canAccessM360Configuration(isGlobalAdmin);

  const keyboardShortcutEntries = useMemo(() => {
    if (keyboardShortcutEntriesProp.length > 0) return keyboardShortcutEntriesProp;

    return buildKeyboardShortcutEntries({
      showGoNav: Boolean(primaryNavGoNav),
      showGlobalSearch: showStaffNav,
      addMenuItems: [],
      canViewMcpPlayground,
      canViewTasks
    });
  }, [canViewMcpPlayground, canViewTasks, keyboardShortcutEntriesProp, primaryNavGoNav, showStaffNav]);

  const openMyTasks = useCallback(() => {
    navigate("/tasks" as Path);
  }, [navigate]);
  useOpenTasksShortcut({
    onOpenTasks: openMyTasks,
    enabled: canViewTasks
  });

  const openMcpPlayground = useCallback(() => {
    navigate("/dev/chat" as Path);
  }, [navigate]);

  const sectionsLink =
    hasPrimarySection && primarySectionId ? (`/sections/${primarySectionId}/jobs/list` as Path) : ("/sections" as Path);

  const organizationLinks = !showStaffNav
    ? []
    : defaultNavLinks(flags)
        .Organization.filter(
          (navLink) => navLink.domain === DOMAIN.EVERYONE || capabilities.domains.includes(navLink.domain)
        )
        .map((link) =>
          link.label === "Sections"
            ? {
                ...link,
                link: { ...link.link, to: sectionsLink } as typeof link.link
              }
            : link
        );

  const manageLinks = !showStaffNav
    ? []
    : defaultNavLinks(flags).Manage.filter(
        (navLink) => navLink.domain === DOMAIN.EVERYONE || capabilities.domains.includes(navLink.domain)
      );

  const desktopNavLinks = [...organizationLinks, ...manageLinks];
  const fallbackVisibleCount = isLargeScreen ? desktopNavLinks.length : organizationLinks.length;
  const { containerRef, measureRef, visibleCount } = useDesktopNavOverflow({
    itemCount: desktopNavLinks.length,
    fallbackVisibleCount,
    measureKey: desktopNavLinks.map((link) => link.label).join("|")
  });
  const visibleDesktopNavLinks = desktopNavLinks.slice(0, visibleCount);
  const overflowDesktopNavLinks = desktopNavLinks.slice(visibleCount);
  const overflowMenuItems: MenuItem[] = overflowDesktopNavLinks.map((link) => ({
    label: link.label,
    onClick: () =>
      navigate(
        hrefWithStoredDefaultTab(link.link.to as string, flags["backoffice-navigation-preferences"] === true) as Path
      )
  }));
  const isOverflowMenuActive = overflowDesktopNavLinks.some((link) =>
    isDesktopNavLinkActive(link.label, link.link.to as string, location.pathname)
  );

  const hasOpenTasks = openTaskCount != null && openTaskCount > 0;
  const tasksAriaLabel = hasOpenTasks ? `Tasks, ${openTaskCount} open` : "Tasks";
  const canViewSystemPreferences = flags["backoffice-system-preferences"] === true;
  const currentUrl = `${location.pathname}${location.search}${location.hash}`;
  const assumeUserUrl = `/assume-user?return_to=${encodeURIComponent(currentUrl)}` as Path;
  const stopAssumingUserAndReturn = () => {
    const returnUrl = getInternalReturnUrl(assumedUser?.returnUrl);
    stopAssumingUser();
    navigate(returnUrl as Path);
  };

  const profileMenuItems: MenuListEntry[] = [
    {
      label: name || "Unknown User",
      supportingText: [email, department].filter(Boolean).join("\n"),
      nonInteractive: true,
      itemClassName: MENU_PANEL_WIDTH_WIDE,
      useDivider: true
    },
    ...(canViewSystemPreferences
      ? [
          {
            label: "System Preferences",
            iconName: "settings" as const,
            onClick: () => navigate("/preferences" as Path),
            dataTestId: "system-preferences-menu-item",
            useDivider: canViewM360Configuration ? false : canViewMcpPlayground || canViewAgentChatLogs
          }
        ]
      : []),
    ...(canViewM360Configuration
      ? [
          {
            label: "Member 360 Configuration",
            iconName: "tune" as const,
            onClick: () => navigate("/configuration" as Path),
            dataTestId: "m360-configuration-menu-item",
            useDivider: canViewMcpPlayground || canViewAgentChatLogs
          }
        ]
      : []),
    ...(canViewMcpPlayground
      ? [
          {
            label: "Chat with Member 360",
            iconName: "smart_toy" as const,
            onClick: openMcpPlayground,
            dataTestId: "mcp-playground-menu-item",
            ...(isMobileDisplay ? {} : { shortcutKey: "?", shortcutShowModifier: false }),
            useDivider: false
          }
        ]
      : []),
    ...(canViewAgentChatLogs
      ? [
          {
            label: "Chat Logs",
            iconName: "forum" as const,
            onClick: () => navigate("/dev/chat-logs" as Path),
            dataTestId: "agent-chat-logs-menu-item",
            useDivider: false
          }
        ]
      : []),
    ...(assumedUser
      ? [
          {
            label: "Stop Assuming User",
            iconName: "logout" as const,
            onClick: stopAssumingUserAndReturn,
            dataTestId: "stop-assuming-user-menu-item",
            useDivider: false
          }
        ]
      : canAssumeUser
        ? [
            {
              label: "Assume a User",
              iconName: "person" as const,
              onClick: () => navigate(assumeUserUrl),
              dataTestId: "assume-user-menu-item",
              useDivider: false
            }
          ]
        : []),
    ...(isMobileDisplay
      ? []
      : [
          {
            label: "Keyboard Shortcuts",
            iconName: "keyboard" as const,
            onClick: () => setKeyboardShortcutsOpen(true),
            dataTestId: "keyboard-shortcuts-menu-item",
            shortcutKey: "?",
            shortcutShowModifier: true,
            shortcutImplicitShift: true,
            useDivider: true
          }
        ])
  ];
  if (isMobileDisplay) {
    const lastProfileItem = profileMenuItems.at(-1);
    if (lastProfileItem && lastProfileItem.type !== "headline") {
      profileMenuItems[profileMenuItems.length - 1] = { ...lastProfileItem, useDivider: true };
    }
  }

  const userMenuItems: MenuListEntry[] = [
    ...profileMenuItems,
    { label: "Logout", iconName: "logout" as const, onClick: logout }
  ];

  const bgColor = interior ? "md:bg-sys-brand-surface bg-sys-brand-surface-container" : "bg-sys-brand-surface";
  const paddingClasses = isMobileDisplay ? "px-3" : "px-3 md:px-12 xl:px-16";

  const desktopGoNavProps = (label: string) =>
    primaryNavGoNav
      ? {
          showChordKeyCaps: primaryNavGoNav.goNavActive,
          modeActive: primaryNavGoNav.goNavActive,
          chordKey: getGoNavChordKey(label),
          dataTestId: `go-nav-${primaryNavItemSlug(label)}`
        }
      : undefined;

  return (
    <>
      <div
        className={classNames(
          paddingClasses,
          "sticky top-0 z-[49] border-b border-sys-brand-outline-variant py-2",
          bgColor
        )}
        data-testid="top-action-bar"
      >
        <div className={`flex w-full min-w-0 items-center ${isMobileDisplay ? "min-w-0 gap-2" : ""}`}>
          {isMobileDisplay ? (
            <div className="lg:hidden z-drawer flex min-w-0 flex-1 items-center gap-2">
              <NavLogo className={`${COMPACT_GLOBAL_NAVBAR_LOGO_SIZE_CLASS} shrink-0`} />
              {primaryNav && showStaffNav && (
                <>
                  <div className="h-6 w-px shrink-0 bg-sys-brand-outline-variant" aria-hidden="true" />
                  <div className="min-w-0">
                    <CompactPrimaryNavMenu
                      tabItems={primaryNav.tabItems}
                      domains={primaryNav.domains}
                      open={primaryNavGoNav?.menuOpen}
                      onOpenChange={primaryNavGoNav?.onMenuOpenChange}
                      showChordKeyCaps={primaryNavGoNav?.goNavActive}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="lg:hidden flex items-center z-drawer flex-shrink-0">
              <NavLogo />
            </div>
          )}

          <div className="hidden lg:flex min-w-0 flex-1 items-center">
            <NavLogo />
            <div className="relative mx-8 min-w-0 flex-1">
              <div
                className="pointer-events-none absolute left-0 top-0 h-0 overflow-hidden"
                aria-hidden="true"
                data-testid="desktop-nav-measure"
                ref={(node) => {
                  node?.toggleAttribute("inert", true);
                }}
              >
                <div ref={measureRef} className="flex w-max flex-nowrap items-center gap-2">
                  {desktopNavLinks.map((link) => (
                    <div key={link.label} className="shrink-0" data-desktop-nav-measure-item="">
                      <NavItem label={link.label} link={link.link} iconName={link.icon} hideIcon measureClone />
                    </div>
                  ))}
                  <div className="shrink-0" data-desktop-nav-measure-more="">
                    <NavItem label="More" iconName="pending" menuItems={[]} hideIcon measureClone />
                  </div>
                </div>
              </div>
              <div
                ref={containerRef}
                className={classNames(
                  "flex min-w-0 flex-1 flex-nowrap items-center gap-2",
                  primaryNavGoNav?.goNavActive && "relative"
                )}
                data-testid="desktop-nav-items"
                data-go-nav-chord-root
              >
                {primaryNavGoNav?.goNavActive && (
                  <GoNavSlidingGhost rootRef={containerRef} active={primaryNavGoNav.goNavActive} />
                )}
                {visibleDesktopNavLinks.map((link) => (
                  <div key={link.label} className="shrink-0">
                    <NavItem
                      label={link.label}
                      link={link.link}
                      iconName={link.icon}
                      active={isDesktopNavLinkActive(link.label, link.link.to as string, location.pathname)}
                      hideIcon
                      goNav={desktopGoNavProps(link.label)}
                    />
                  </div>
                ))}
                {overflowMenuItems.length > 0 && (
                  <div className="shrink-0" data-testid="desktop-nav-more">
                    <NavItem
                      label="More"
                      iconName="pending"
                      menuItems={overflowMenuItems}
                      active={isOverflowMenuActive}
                      hideIcon
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={`ml-auto flex shrink-0 items-center ${isMobileDisplay ? compactGlobalToolbarActionsGapClassName : "gap-3"}`}
            data-testid="top-action-bar-actions"
          >
            <div
              id="icons"
              className={`flex ${isMobileDisplay ? `shrink-0 items-center ${compactGlobalToolbarIconClusterGapClassName}` : "gap-3"}`}
            >
              {actions}
              <BackendSourceIndicator />
              {canViewTasks ? (
                <div className="relative shrink-0" data-testid="global-tasks-control">
                  {isMobileDisplay ? (
                    <GlobalToolbarIconButton
                      icon="assignment"
                      ariaLabel={tasksAriaLabel}
                      ariaKeyShortcuts="t"
                      testId="global-tasks-button"
                      onClick={openMyTasks}
                    />
                  ) : (
                    <ActionShortcutTooltip
                      label="Tasks"
                      hints={[{ keyLabel: "t", showModifier: false, testId: "global-tasks-shortcut-hint" }]}
                    >
                      <Button
                        variant="squared"
                        icon="assignment"
                        iconWeight={800}
                        testId="global-tasks-button"
                        ariaLabel={tasksAriaLabel}
                        ariaKeyShortcuts="t"
                        onClick={openMyTasks}
                      />
                    </ActionShortcutTooltip>
                  )}
                  {hasOpenTasks ? (
                    <span aria-hidden="true">
                      <Badge
                        value={openTaskCount}
                        className="absolute -top-1 -right-1 pointer-events-none"
                        testId="global-tasks-badge"
                      />
                    </span>
                  ) : null}
                </div>
              ) : null}
              {isMobileDisplay ? (
                <GlobalToolbarIconButton
                  icon="question_mark"
                  ariaLabel="Help"
                  testId="global-help-button"
                  onClick={openFeedback}
                />
              ) : (
                <ActionShortcutTooltip label="Help" hints={[]}>
                  <Button
                    variant="squared"
                    icon="question_mark"
                    iconWeight={800}
                    testId="global-help-button"
                    ariaLabel="Help"
                    onClick={openFeedback}
                  />
                </ActionShortcutTooltip>
              )}
            </div>
            <Menu
              yOffset={8}
              AnchorComponent={
                <div className={`relative ${isMobileDisplay ? "shrink-0" : ""}`} data-testid="user-profile-menu">
                  <Avatar
                    picture={picture}
                    size={isMobileDisplay ? "small" : "medium"}
                    className={isMobileDisplay ? COMPACT_GLOBAL_NAVBAR_AVATAR_SIZE_CLASS : undefined}
                  />
                </div>
              }
              menuItems={userMenuItems}
            />
          </div>
        </div>
      </div>
      <KeyboardShortcutsDialog
        open={keyboardShortcutsOpen}
        onOpenChange={setKeyboardShortcutsOpen}
        entries={keyboardShortcutEntries}
      />
      {feedbackOpen ? <M360FeedbackDialog open onClosed={() => setFeedbackOpen(false)} /> : null}
    </>
  );
}
